import React, { useState, useRef, useContext, useEffect } from "react";

import { motion, AnimatePresence } from "framer-motion";

import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";

import LoaderIcon from "../images/LoaderIconInline";
import SelectMenu from "../controls/SelectMenu";

// DOWNLOADER
import html2canvas from "html2canvas";
import { saveAs } from "file-saver";

function base64Resize(sourceBase64, scale, fileName) {
   const _scale = scale;
   var img = document.createElement("img");
   img.setAttribute("src", sourceBase64);

   img.onload = () => {
      var canvas = document.createElement("canvas");
      canvas.width = img.width * _scale;
      canvas.height = img.height * _scale;

      var ctx = canvas.getContext("2d");
      var cw = canvas.width;
      var ch = canvas.height;
      var maxW = img.width * _scale;
      var maxH = img.height * _scale;

      var iw = img.width;
      var ih = img.height;
      var scl = Math.min(maxW / iw, maxH / ih);
      var iwScaled = iw * scl;
      var ihScaled = ih * scl;
      canvas.width = iwScaled;
      canvas.height = ihScaled;
      ctx.drawImage(img, 0, 0, iwScaled, ihScaled);

      canvas.toBlob(function (blob) {
         saveAs(blob, fileName + ".png");
      }, "image/png");
   };
}

export default function Downloader({ enabled, downloadName, children, resolution: initialResolution, onResolutionChange }) {
   const [downloadVisibility, setDownloadVisibility] = useState(false);
   const [resolution, setResolution] = useState(initialResolution || "1920x1080");
   const canvasRef = useRef();

   // Sync resolution when prop changes
   useEffect(() => {
      if (initialResolution && initialResolution !== resolution) {
         setResolution(initialResolution);
      }
   }, [initialResolution]);

   const handleResolutionChange = (newResolution) => {
      setResolution(newResolution);
      if (onResolutionChange) {
         onResolutionChange(newResolution);
      }
   };

   // DOWNLOAD
   useEffect(() => {
      if (downloadVisibility) {
         const [width, height] = resolution.split("x").map(Number);
         // Store original devicePixelRatio to restore later
         const originalDevicePixelRatio = window.devicePixelRatio;
         
         // Capture at exact resolution
         html2canvas(canvasRef.current, {
            useCORS: true,
            logging: false,
            backgroundColor: null,
            scale: 1,
            width: width,
            height: height,
         })
            .then((canvas) => {
               // Create output canvas at exact resolution
               const outputCanvas = document.createElement('canvas');
               outputCanvas.width = width;
               outputCanvas.height = height;
               const ctx = outputCanvas.getContext('2d');
               
               // Draw captured canvas to output at exact size (no scaling)
               ctx.drawImage(canvas, 0, 0, width, height);
               
               // Save at exact resolution (1x scale, not 2x)
               outputCanvas.toBlob(function (blob) {
                  saveAs(blob, downloadVisibility + ".png");
               }, "image/png");
               
               // Restore original devicePixelRatio
               window.devicePixelRatio = originalDevicePixelRatio;
               
               setDownloadVisibility(false);
            })
            .catch((error) => {
               console.error('Download error:', error);
               window.devicePixelRatio = originalDevicePixelRatio;
               setDownloadVisibility(false);
            });
      }
   }, [downloadVisibility, resolution]);

   const handleDownload = async (event) => {
      setDownloadVisibility(event.target.value);
   };

   // Parse resolution for download window
   const [downloadWidth, downloadHeight] = resolution.split("x").map(Number);

   return (
      <>
         {/* Hide Downloader button if Audio Tone, Gradient, or Snake pattern is selected */}
         {downloadName !== "TestPattern_audioTone" && downloadName !== "TestPattern_movingGradient" && downloadName !== "TestPattern_snake" && (
            <Button
               onClick={handleDownload}
               variant={enabled ? "contained" : "disabled"}
               color='error'
               sx={{
                  position: "relative",
                  zIndex: 100,
                  width: 200,
                  height: 50,
               }}
               value={downloadName}
            >
               DOWNLOAD
            </Button>
         )}

         <Box
            id='downloadWindow'
            sx={{
               position: "fixed",
               top: 0,
               left: 0,
               width: "100%",
               height: "100%",
               zIndex: 1500,
               backgroundColor: "rgba(0,0,0,.8)",
               visibility: downloadVisibility ? "visible" : "hidden",
            }}
         >
            <Stack
               alignItems='center'
               justifyContent='center'
               sx={{
                  width: "100%",
                  height: "100%",
                  position: "absolute",
                  top: 0,
                  left: 0,
                  zIndex: 1500,
                  backgroundColor: "#333",
                  color: "#ccc",
               }}
            >
               <Typography
                  variant='h3'
                  component='div'
                  sx={{ textTransform: "uppercase", mt: 4, mb: 2 }}
               >
                  DOWNLOADING...
               </Typography>
               <LoaderIcon size='large' />
            </Stack>
            <Box
               className='transparentBG'
               sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: `${downloadWidth || 1920}px`,
                  height: `${downloadHeight || 1080}px`,
                  zIndex: 1000,
                  transformOrigin: "top left",
                  overflow: "hidden",
                  border: "1px solid #333",
                  boxSizing: "border-box",
               }}
            >
               <Box
                  sx={{
                     position: "absolute",
                     width: "100%",
                     height: "100%",
                     top: 0,
                     left: 0,
                     transformOrigin: "top left",
                     overflow: "hidden",
                  }}
                  ref={canvasRef}
               >
                  {children}
               </Box>
            </Box>
         </Box>
      </>
   );
}
